import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { BookIndex } from '../types/book';
import { BookApiService } from '../services/bookApi';
import { TTSCacheService } from '../services/ttsCache';
import { ReadApiService } from '../services/readApi';
import { getUserId } from '../utils/userId';
import ChapterList from './ChapterList';
import VerseList from './VerseList';

type ViewState = 'books' | 'chapters' | 'verses';

interface BookReaderProps {
  onGoHome?: () => void;
}

export interface BookReaderRef {
  handleBackToBooks: () => void;
}

const BookReader = forwardRef<BookReaderRef, BookReaderProps>(({ onGoHome }, ref) => {
  const [currentView, setCurrentView] = useState<ViewState>('books');
  const [selectedBook, setSelectedBook] = useState<BookIndex | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [bookIndices, setBookIndices] = useState<BookIndex[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [cacheSize, setCacheSize] = useState<number>(0);
  const [isDeletingCache, setIsDeletingCache] = useState<boolean>(false);
  const [readStatusCount, setReadStatusCount] = useState<number>(0);
  const [isDeletingReadStatus, setIsDeletingReadStatus] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showTtsTooltip, setShowTtsTooltip] = useState<boolean>(false);
  const [showReadTooltip, setShowReadTooltip] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    handleBackToBooks
  }));

  useEffect(() => {
    const fetchBookIndices = async () => {
      try {
        setLoading(true);
        setError(null);
        const indices = await BookApiService.getBookIndices();
        setBookIndices(indices);
        
        // 캐시 크기 조회
        const size = await TTSCacheService.getCacheSize();
        setCacheSize(size);
        
        // 읽음 상태 개수는 API에서 관리하므로 0으로 설정
        setReadStatusCount(0);
      } catch (err) {
        setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookIndices();
  }, []);

  // 화면 크기 감지
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  const handleBookSelect = (book: BookIndex) => {
    setSelectedBook(book);
    setCurrentView('chapters');
    // 모바일에서 스크롤 후 렌더링 문제 해결을 위한 강제 리렌더링
    if (isMobile) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        // 뷰포트 강제 업데이트
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
        }
      }, 100);
    }
  };

  const handleChapterSelect = (book: BookIndex, chapter: number) => {
    setSelectedBook(book);
    setSelectedChapter(chapter);
    setCurrentView('verses');
    // 모바일에서 스크롤 후 렌더링 문제 해결을 위한 강제 리렌더링
    if (isMobile) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        // 뷰포트 강제 업데이트
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
        }
      }, 100);
    }
  };

  const handleBackToBooks = () => {
    setCurrentView('books');
    setSelectedBook(null);
    setSelectedChapter(null);
    // 모바일에서 스크롤 후 렌더링 문제 해결을 위한 강제 리렌더링
    if (isMobile) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        // 뷰포트 강제 업데이트
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
        }
      }, 100);
    }
    if (onGoHome) {
      onGoHome();
    }
  };

  const handleBackToChapters = () => {
    setCurrentView('chapters');
    setSelectedChapter(null);
  };

  const handleClearCache = async () => {
    if (window.confirm('모든 음성 캐시를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      try {
        setIsDeletingCache(true);
        await TTSCacheService.clearAllCache();
        setCacheSize(0);
        alert('음성 캐시가 모두 삭제되었습니다.');
      } catch (error) {
        console.error('캐시 삭제 중 오류:', error);
        alert('캐시 삭제 중 오류가 발생했습니다.');
      } finally {
        setIsDeletingCache(false);
      }
    }
  };

  const handleClearReadStatus = async () => {
    if (window.confirm('모든 읽음 상태를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      try {
        setIsDeletingReadStatus(true);
        await ReadApiService.deleteAllReadStatus();
        setReadStatusCount(0);
        alert('읽음 상태가 모두 삭제되었습니다.');
      } catch (error) {
        console.error('읽음 상태 삭제 중 오류:', error);
        // 오류 시 화면에 표시하지 않고 콘솔에만 출력
      } finally {
        setIsDeletingReadStatus(false);
      }
    }
  };

  const handlePreviousChapter = () => {
    if (!selectedBook || !selectedChapter) return;
    
    if (selectedChapter > 1) {
      // 같은 Book의 이전 장
      setSelectedChapter(selectedChapter - 1);
    } else {
      // 이전 Book의 마지막 장
      const currentBookIndex = bookIndices.findIndex(book => book.bookNo === selectedBook.bookNo);
      if (currentBookIndex > 0) {
        const previousBook = bookIndices[currentBookIndex - 1];
        setSelectedBook(previousBook);
        setSelectedChapter(previousBook.chapters);
      }
    }
  };

  const handleNextChapter = () => {
    if (!selectedBook || !selectedChapter) return;
    
    if (selectedChapter < selectedBook.chapters) {
      // 같은 Book의 다음 장
      setSelectedChapter(selectedChapter + 1);
    } else {
      // 다음 Book의 첫 장
      const currentBookIndex = bookIndices.findIndex(book => book.bookNo === selectedBook.bookNo);
      if (currentBookIndex < bookIndices.length - 1) {
        const nextBook = bookIndices[currentBookIndex + 1];
        setSelectedBook(nextBook);
        setSelectedChapter(1);
      }
    }
  };

  if (currentView === 'chapters' && selectedBook) {
    return (
      <ChapterList 
        book={selectedBook} 
        onChapterSelect={handleChapterSelect} 
        onBack={handleBackToBooks} 
      />
    );
  }

  if (currentView === 'verses' && selectedBook && selectedChapter) {
    return (
      <VerseList 
        book={selectedBook} 
        chapter={selectedChapter} 
        onBack={handleBackToChapters}
        onGoHome={handleBackToBooks}
        onPreviousChapter={handlePreviousChapter}
        onNextChapter={handleNextChapter}
      />
    );
  }

  // Book 목록 표시
  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Book 인덱스를 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <p>오류가 발생했습니다: {error}</p>
        <button 
          onClick={() => window.location.reload()} 
          style={{ marginTop: '10px', padding: '8px 16px' }}
        >
          다시 시도
        </button>
      </div>
    );
  }

  const userId = getUserId();

  return (
    <div style={{ padding: '20px' }}>
      {userId && (
        <div style={{
          textAlign: 'center',
          marginBottom: '15px',
          fontSize: '14px',
          color: '#666',
          opacity: 0.8
        }}>
          {userId}
        </div>
      )}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: isMobile ? '8px' : '15px' 
      }}>
        {bookIndices
          .map((book) => {
            const hasNameEN = book.nameEN && book.nameEN !== 'null' && book.nameEN.trim() !== '';
            const hasNameZH = book.nameZH && book.nameZH !== 'null' && book.nameZH.trim() !== '';
            const displayName = hasNameEN ? book.nameEN : (hasNameZH ? book.nameZH : '');
            
            return (
              <div 
                key={book.bookNo} 
                onClick={() => handleBookSelect(book)}
                style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: isMobile ? '6px' : '8px', 
                  padding: isMobile ? '12px 16px' : '20px',
                  backgroundColor: book.bookNo <= 39 ? '#f8f9fa' : '#e8f4fd',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: isMobile ? '60px' : 'auto',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = book.bookNo <= 39 ? '#e9ecef' : '#d1ecf1';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = book.bookNo <= 39 ? '#f8f9fa' : '#e8f4fd';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <h3 style={{ 
                    margin: '0', 
                    color: book.bookNo <= 39 ? '#8b4513' : '#1e40af', 
                    fontSize: isMobile ? '16px' : '20px',
                    fontWeight: 'bold'
                  }}>
                    {book.name}
                  </h3>
                  {displayName && (
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      fontSize: isMobile ? '12px' : '14px', 
                      color: '#6c757d',
                      fontWeight: 'normal'
                    }}>
                      {displayName}
                    </p>
                  )}
                </div>
                <div style={{ 
                  fontSize: isMobile ? '12px' : '14px', 
                  color: '#6c757d',
                  fontWeight: 'normal'
                }}>
                  {book.chapters}장
                </div>
              </div>
            );
          })}
      </div>
      
      {/* 캐시 관리 */}
      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '10px' }}>
              <p style={{ margin: '0', fontSize: '14px', color: '#6c757d' }}>
                음성 캐시: {cacheSize}개 파일
              </p>
              <div style={{ position: 'relative' }}>
                <span 
                  style={{ 
                    cursor: 'pointer', 
                    color: '#007bff', 
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={() => setShowTtsTooltip(true)}
                  onMouseLeave={() => setShowTtsTooltip(false)}
                >
                  ?
                </span>
                {showTtsTooltip && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#333',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    zIndex: 1000,
                    marginBottom: '5px',
                    width: '300px',
                    whiteSpace: 'normal',
                    textAlign: 'center'
                  }}>
                    음성캐시는 한번 읽은 절에 대해 빠르게 음성을 재생하기 위해 필요합니다. 특별한 이유가 없으면 삭제하지 않으셔도 됩니다.
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleClearCache}
              disabled={isDeletingCache || cacheSize === 0}
              style={{
                padding: '8px 16px',
                backgroundColor: isDeletingCache || cacheSize === 0 ? '#e9ecef' : '#dc3545',
                color: isDeletingCache || cacheSize === 0 ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isDeletingCache || cacheSize === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {isDeletingCache ? '삭제 중...' : '음성 캐시 삭제'}
            </button>
          </div>
          
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '10px' }}>
              <p style={{ margin: '0', fontSize: '14px', color: '#6c757d' }}>
                읽음 상태: {readStatusCount}개 절
              </p>
              <div style={{ position: 'relative' }}>
                <span 
                  style={{ 
                    cursor: 'pointer', 
                    color: '#007bff', 
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                  onMouseEnter={() => setShowReadTooltip(true)}
                  onMouseLeave={() => setShowReadTooltip(false)}
                >
                  ?
                </span>
                {showReadTooltip && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#333',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    zIndex: 1000,
                    marginBottom: '5px',
                    width: '250px',
                    whiteSpace: 'normal',
                    textAlign: 'center'
                  }}>
                    절의 읽음 상태를 초기화 하시려면 읽음캐시삭제 버튼을 눌러주세요.
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleClearReadStatus}
              disabled={isDeletingReadStatus}
              style={{
                padding: '8px 16px',
                backgroundColor: isDeletingReadStatus ? '#e9ecef' : '#dc3545',
                color: isDeletingReadStatus ? '#6c757d' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isDeletingReadStatus ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {isDeletingReadStatus ? '삭제 중...' : '읽음 캐시 삭제'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

BookReader.displayName = 'BookReader';

export default BookReader;
